/**
 * i18n setup — vue-i18n v9 with eager-loaded locales for now (small enough,
 * keeps SSR-friendly). Switch to lazy-load if bundle size becomes an issue.
 */

import { createI18n } from 'vue-i18n';
import type { WritableComputedRef } from 'vue';
import type { Locale } from '@qb/shared';
import { SUPPORTED_LOCALES, isRtl } from '@qb/shared';

import en from './en.json';
import he from './he.json';
import pt from './pt.json';
import ru from './ru.json';

export type Messages = typeof en;

const STORAGE_KEY = 'qb.locale';

export function getInitialLocale(): Locale {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
    const nav = (window.navigator.language || 'en').slice(0, 2).toLowerCase();
    if ((SUPPORTED_LOCALES as readonly string[]).includes(nav)) {
      return nav as Locale;
    }
  }
  return 'en';
}

export function persistLocale(locale: Locale): void {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, locale);
  }
}

export function applyDir(locale: Locale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  document.documentElement.dir = isRtl(locale) ? 'rtl' : 'ltr';
}

const initial = getInitialLocale();
applyDir(initial);

export const i18n = createI18n<[Messages], Locale>({
  legacy: false,
  globalInjection: true,
  locale: initial,
  fallbackLocale: 'en',
  messages: { en, he, pt, ru },
  // Translation tokens may contain `{count}` etc.; turn off warnings for empties.
  missingWarn: false,
  fallbackWarn: false,
});

export function setLocale(locale: Locale): void {
  // With `legacy: false`, `i18n.global.locale` is a WritableComputedRef<string>
  // at runtime, but the createI18n() return type union can resolve to the
  // legacy VueI18n shape where `locale` is a plain string. Narrow explicitly.
  const localeRef = i18n.global.locale as unknown as WritableComputedRef<Locale>;
  localeRef.value = locale;
  persistLocale(locale);
  applyDir(locale);
}
